import { useLanguage } from '@context/LanguageContext'
import { useDocumentMetadata } from '@hooks/useDocumentMetadata'
import { cx } from '@utils/cx'
import StaticLayout from '../layout/StaticLayout'
import { SupportIcon, KofiIcon, PaypalIcon, HostingIcon, WebIcon, DevelopmentIcon, TimeIcon, CodeIcon } from '../interface/Icons'
import { useNamespaces } from '@hooks/useNamespaces'
import { ROUTE_NAMESPACES } from '@i18n/namespaces'
import ScreenFallback from '@components/layout/ScreenFallback'
import styles from './Support.module.css'

export default function Support () {
  const { t, tFunc } = useLanguage()
  const { isLoading: namespacesLoading } = useNamespaces(ROUTE_NAMESPACES.support)
  useDocumentMetadata()

  if (namespacesLoading) {
    return <ScreenFallback />
  }

  const handleKofiClick = () => {
    window.open('https://ko-fi.com/memodiez', '_blank', 'noopener,noreferrer')
  }

  const handlePaypalClick = (amount) => {
    window.open(
      `https://www.paypal.com/donate?business=donate@memodiez.com&amount=${amount}&currency_code=USD`,
      '_blank',
      'noopener,noreferrer'
    )
  }

  return (
    <StaticLayout>
      <div className={styles.supportPage}>
        {/* Hero Section */}
        <section className={styles.hero}>
          <h1 className={styles.heroTitle}>{t.support.hero.title}</h1>
          <p className={styles.heroDescription}>{t.support.hero.description}</p>
        </section>

        {/* Transparency Section */}
        <section className={cx(styles.section, styles.transparency)}>
          <h2 className={styles.sectionTitle}>{t.support.transparency.title}</h2>
          <p className={styles.intro}>{t.support.transparency.intro}</p>
          <ul className={styles.transparencyList}>
            {t.support.transparency.items.map((item, index) => {
              const icons = [HostingIcon, WebIcon, DevelopmentIcon, TimeIcon, CodeIcon]
              const IconComponent = icons[index % icons.length]
              return (
                <li key={index} className={styles.transparencyItem}>
                  <IconComponent className={styles.transparencyIcon} size={20} stroke={1.5} />
                  <span className={styles.transparencyText}>{item}</span>
                </li>
              )
            })}
          </ul>
        </section>

        {/* Platforms Section */}
        <section className={cx(styles.section, styles.platforms)}>
          <h2 className={styles.sectionTitle}>{t.support.platforms.title}</h2>

          <div className={styles.platformsGrid}>
            {/* Ko-fi Platform */}
            <div className={styles.platform}>
              <div className={styles.platformInfo}>
                <h3 className={styles.platformTitle}>
                  <KofiIcon className={styles.platformIcon} size={20} stroke={1.5} />
                  {t.support.platforms.kofi.title}
                </h3>
                <p className={styles.platformDescription}>{t.support.platforms.kofi.description}</p>
              </div>
              <button
                type='button'
                onClick={handleKofiClick}
                className={cx(styles.platformBtn, styles.platformBtnKofi)}
                aria-label={tFunc('support.access.kofiButton').replace('{action}', t.support.platforms.kofi.button)}
              >
                <span className={styles.platformText}>{t.support.platforms.kofi.button}</span>
              </button>
            </div>

            {/* PayPal Platform */}
            <div className={styles.platform}>
              <div className={styles.platformInfo}>
                <h3 className={styles.platformTitle}>
                  <PaypalIcon className={styles.platformIcon} size={20} stroke={1.5} />
                  {t.support.platforms.paypal.title}
                </h3>
                <p className={styles.platformDescription}>{t.support.platforms.paypal.description}</p>
              </div>
              <div className={styles.paypalButtons}>
                {[5, 10, 20, 50].map(amount => (
                  <button
                    type='button'
                    key={amount}
                    onClick={() => handlePaypalClick(amount)}
                    className={styles.paypalBtn}
                    aria-label={tFunc('support.access.paypalButton').replace('{amount}', `$${amount}`)}
                  >
                    ${amount}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Community Section */}
        <section className={cx(styles.section, styles.community)}>
          <h2 className={styles.sectionTitle}>{t.support.community.title}</h2>
          <p className={styles.communityDescription}>{t.support.community.description}</p>
          <SupportIcon size={24} stroke={1.5} className={styles.heartIcon} />
        </section>

        {/* Disclaimer */}
        <footer className={styles.disclaimer}>
          <p className={styles.disclaimerText}>{t.support.disclaimer}</p>
        </footer>
      </div>
    </StaticLayout>
  )
}
